// Normalizes a request URL/path into a stable "bucket" for telemetry.
// Rules:
// - strip scheme/host and any API prefix (/api/v1 or /public/api/v1)
// - ignore query string
// - collapse dynamic segments: ISO dates -> :date, UUID/HEX -> :id, integers -> :id
// - drop trailing placeholders so `/company/timetable/day-report/2025-11-11`
//   becomes `company/timetable/day-report`
export function bucketFromUrl(raw: string): string {
  let path = raw;
  try {
    // Accept raw paths or absolute URLs
    const u = new URL(raw, window.location.origin);
    path = u.pathname; // ignore query params by design
  } catch {
    // raw is already a pathname; fall through
  }

  // Remove leading API prefixes
  path = path.replace(/^\/(public\/)?api\/v\d+\//, "/");
  // Collapse duplicate slashes and trim
  path = path.replace(/\/{2,}/g, "/").replace(/^\/|\/$/g, "");

  const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;                     // 2025-11-11
  const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const HEX_RE = /^[0-9a-f]{16,}$/i;                             // long hex ids
  const INT_RE = /^\d+$/;

  const parts = path.split("/").filter(Boolean).map(seg => {
    if (ISO_DATE_RE.test(seg)) return ":date";
    if (UUID_RE.test(seg) || HEX_RE.test(seg) || INT_RE.test(seg)) return ":id";
    return seg;
  });

  // Drop trailing placeholders to match our desired buckets
  while (parts.length && /^:(id|date)$/.test(parts[parts.length - 1])) {
    parts.pop();
  }
  return parts.join("/");
}

// Convenience for method + bucket span name
export function spanName(method: string | undefined, bucket: string): string {
  const m = (method || "GET").toUpperCase();
  return `${m} ${bucket || "/"}`;
}

// --- NEW: endpoints that should be further separated by the number of `dates` params
export const DATES_SENSITIVE_BUCKETS = new Set<string>([
  "company/timetable/schedule",
  "company/timetable/attendance_day",
]);

// Extract a " (dates:N)" suffix for span descriptions when applicable.
// - Counts values from: `?dates=...`, repeated `?dates=...&dates=...`,
//   array-style `?dates[]=...`, indexed `?dates[0]=...`, and comma-separated lists.
export function datesVariantSuffix(rawUrl: string, bucket: string):
  { suffix: string; count?: number } {
  if (!DATES_SENSITIVE_BUCKETS.has(bucket)) return { suffix: "" };

  let u: URL | null = null;
  try { u = new URL(rawUrl, window.location.origin); } catch { /* ignore */ }
  if (!u) return { suffix: "" };

  const sp = u.searchParams;
  let count = 0;

  // direct: dates, dates[], repeated, comma-separated
  const gather = (key: string) => {
    for (const v of sp.getAll(key)) {
      if (v === null || v === undefined) continue;
      const parts = String(v).split(",").filter(Boolean);
      if (parts.length > 0) count += parts.length;
    }
  };
  gather("dates");
  gather("dates[]");

  // indexed: dates[0], dates[1], ...
  for (const [k, v] of sp.entries()) {
    if (/^dates\[\d+\]$/.test(k)) {
      const parts = String(v ?? "").split(",").filter(Boolean);
      count += parts.length ? parts.length : 1;
    }
  }

  if (!count) return { suffix: "" };
  return { suffix: ` (dates:${count})`, count };
}
